import React, { useState, useCallback, useMemo, useRef } from 'react'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { useNls } from '../../nls/nlsProvider'
import './treeList.css'

export interface TreeListNode<T = unknown> {
  id: string
  label: string
  code?: string
  levelLabel?: string
  children?: TreeListNode<T>[]
  data?: T
  disabled?: boolean
  archived?: boolean
  icon?: React.ReactNode
}

export interface TreeListProps<T = unknown> {
  nodes: TreeListNode<T>[]
  selectedId?: string | null
  onSelect?: (node: TreeListNode<T>) => void
  expandedIds?: string[]
  defaultExpandedIds?: string[]
  onToggleExpand?: (node: TreeListNode<T>, expanded: boolean) => void
  renderLevelChip?: (node: TreeListNode<T>, depth: number) => React.ReactNode
  renderIcon?: (node: TreeListNode<T>) => React.ReactNode
  renderActions?: (node: TreeListNode<T>) => React.ReactNode
  ariaLabel?: string
  className?: string
  emptyMessage?: string
  indentPx?: number
}

interface FlatItem<T> {
  node: TreeListNode<T>
  depth: number
  hasChildren: boolean
  isExpanded: boolean
  parentPath: string[]
}

export function TreeList<T = unknown>({
  nodes,
  selectedId,
  onSelect,
  expandedIds: controlledExpandedIds,
  defaultExpandedIds = [],
  onToggleExpand,
  renderLevelChip,
  renderIcon,
  renderActions,
  ariaLabel,
  className = '',
  emptyMessage,
  indentPx = 20,
}: TreeListProps<T>): React.ReactElement {
  const { t } = useNls()
  const resolvedAriaLabel = ariaLabel ?? t('NLSCommonTreeListAriaLabel')
  const resolvedEmptyMessage = emptyMessage ?? t('NLSCommonTreeListEmpty')

  const isControlled = controlledExpandedIds !== undefined
  const [internalExpandedIds, setInternalExpandedIds] = useState<Set<string>>(
    () => new Set(defaultExpandedIds),
  )

  const activeExpandedIds = useMemo(() => {
    return isControlled ? new Set(controlledExpandedIds) : internalExpandedIds
  }, [isControlled, controlledExpandedIds, internalExpandedIds])

  const toggleExpand = useCallback(
    (node: TreeListNode<T>) => {
      const willBeExpanded = !activeExpandedIds.has(node.id)
      if (!isControlled) {
        setInternalExpandedIds((prev) => {
          const next = new Set(prev)
          if (willBeExpanded) {
            next.add(node.id)
          } else {
            next.delete(node.id)
          }
          return next
        })
      }
      onToggleExpand?.(node, willBeExpanded)
    },
    [activeExpandedIds, isControlled, onToggleExpand],
  )

  // Flatten visible nodes
  const flattenedList: FlatItem<T>[] = useMemo(() => {
    const result: FlatItem<T>[] = []

    const traverse = (items: TreeListNode<T>[], depth: number, parentPath: string[]) => {
      for (const item of items) {
        const hasChildren = Boolean(item.children && item.children.length > 0)
        const isExpanded = activeExpandedIds.has(item.id)
        result.push({
          node: item,
          depth,
          hasChildren,
          isExpanded,
          parentPath,
        })
        if (hasChildren && isExpanded && item.children) {
          traverse(item.children, depth + 1, [...parentPath, item.id])
        }
      }
    }

    traverse(nodes, 0, [])
    return result
  }, [nodes, activeExpandedIds])

  const [focusedId, setFocusedId] = useState<string | null>(() => {
    if (selectedId) return selectedId
    return flattenedList[0]?.node.id ?? null
  })

  // Synchronize focus if selectedId changes
  const [prevSelectedId, setPrevSelectedId] = useState(selectedId)
  if (selectedId !== prevSelectedId) {
    setPrevSelectedId(selectedId)
    if (selectedId && flattenedList.some((f) => f.node.id === selectedId)) {
      setFocusedId(selectedId)
    }
  }

  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  const setItemRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) {
      itemRefs.current.set(id, el)
    } else {
      itemRefs.current.delete(id)
    }
  }, [])

  const focusItem = useCallback((id: string) => {
    setFocusedId(id)
    const el = itemRefs.current.get(id)
    el?.focus()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, currentItem: FlatItem<T>, currentIndex: number) => {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        if (currentIndex < flattenedList.length - 1) {
          focusItem(flattenedList[currentIndex + 1].node.id)
        }
        break
      }
      case 'ArrowUp': {
        e.preventDefault()
        if (currentIndex > 0) {
          focusItem(flattenedList[currentIndex - 1].node.id)
        }
        break
      }
      case 'ArrowRight': {
        e.preventDefault()
        if (currentItem.hasChildren) {
          if (!currentItem.isExpanded) {
            toggleExpand(currentItem.node)
          } else if (currentIndex < flattenedList.length - 1) {
            // Focus first child
            focusItem(flattenedList[currentIndex + 1].node.id)
          }
        }
        break
      }
      case 'ArrowLeft': {
        e.preventDefault()
        if (currentItem.hasChildren && currentItem.isExpanded) {
          toggleExpand(currentItem.node)
        } else if (currentItem.parentPath.length > 0) {
          // Focus parent
          const parentId = currentItem.parentPath[currentItem.parentPath.length - 1]
          focusItem(parentId)
        }
        break
      }
      case 'Home': {
        e.preventDefault()
        if (flattenedList.length > 0) {
          focusItem(flattenedList[0].node.id)
        }
        break
      }
      case 'End': {
        e.preventDefault()
        if (flattenedList.length > 0) {
          focusItem(flattenedList[flattenedList.length - 1].node.id)
        }
        break
      }
      case 'Enter':
      case ' ': {
        e.preventDefault()
        if (!currentItem.node.disabled) {
          onSelect?.(currentItem.node)
        }
        break
      }
      default:
        break
    }
  }

  if (nodes.length === 0) {
    return (
      <div className={`treeList treeList--empty ${className}`} role="tree" aria-label={resolvedAriaLabel}>
        <div className="treeList__empty">{resolvedEmptyMessage}</div>
      </div>
    )
  }

  return (
    <div
      className={`treeList ${className}`}
      role="tree"
      aria-label={resolvedAriaLabel}
    >
      {flattenedList.map((item, index) => {
        const isSelected = selectedId === item.node.id
        const isFocused = focusedId === item.node.id || (focusedId === null && index === 0)
        const depth = item.depth
        const paddingLeft = 8 + depth * indentPx

        return (
          <div
            key={item.node.id}
            ref={(el) => setItemRef(item.node.id, el)}
            role="treeitem"
            id={`treeitem-${item.node.id}`}
            aria-level={depth + 1}
            aria-expanded={item.hasChildren ? item.isExpanded : undefined}
            aria-selected={isSelected}
            aria-disabled={item.node.disabled ? true : undefined}
            tabIndex={isFocused ? 0 : -1}
            className={`treeList__item ${isSelected ? 'treeList__item--selected' : ''} ${
              item.node.disabled ? 'treeList__item--disabled' : ''
            } ${item.node.archived ? 'treeList__item--archived' : ''}`}
            style={{ paddingLeft: `${paddingLeft}px` }}
            onClick={() => {
              if (!item.node.disabled) {
                focusItem(item.node.id)
                onSelect?.(item.node)
              }
            }}
            onKeyDown={(e) => handleKeyDown(e, item, index)}
          >
            {item.hasChildren ? (
              <button
                type="button"
                className="treeList__toggle"
                tabIndex={-1}
                aria-label={
                  item.isExpanded
                    ? t('NLSCommonTreeListCollapse', { label: item.node.label })
                    : t('NLSCommonTreeListExpand', { label: item.node.label })
                }
                onClick={(e) => {
                  e.stopPropagation()
                  toggleExpand(item.node)
                }}
              >
                {item.isExpanded ? (
                  <ExpandMoreRoundedIcon fontSize="small" />
                ) : (
                  <ChevronRightRoundedIcon fontSize="small" />
                )}
              </button>
            ) : (
              <span className="treeList__toggleSpacer" aria-hidden="true" />
            )}

            {(renderIcon || item.node.icon) && (
              <span className="treeList__icon" aria-hidden="true">
                {renderIcon ? renderIcon(item.node) : item.node.icon}
              </span>
            )}

            <div className="treeList__content">
              {renderLevelChip ? (
                renderLevelChip(item.node, depth)
              ) : item.node.levelLabel ? (
                <span className="treeList__chip">{item.node.levelLabel}</span>
              ) : null}

              <span className="treeList__label">{item.node.label}</span>
            </div>

            {renderActions && (
              <div
                className="treeList__actions"
                onClick={(e) => e.stopPropagation()}
              >
                {renderActions(item.node)}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
